// 1.51.0.0 (signalement du club "la page de maintenance n'apparait plus pendant la
// reconstruction"). La page de secours de SWAG (cesam-maintenance.html, sur les 502/503/504) ne
// se voit qu'au CHARGEMENT d'une page. Or le deploiement construit les images avant de
// redemarrer les conteneurs : l'interface reste servie, seul le serveur d'API redemarre une
// vingtaine de secondes, et ses 502 arrivaient sur des appels en arriere-plan, avales sans rien
// dire (les journaux de SWAG montrent bien la page de secours renvoyee... a des appels /api).
//
// Ici : tout appel qui revient en 502/503/504, ou sans reponse du tout, ouvre un ecran "mise a
// jour en cours" POSE PAR-DESSUS la page (rien n'est demonte : une saisie en cours reste a
// l'ecran), qui interroge /api/health toutes les 3 secondes et se ferme seul au retour.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const API = `${import.meta.env.VITE_BACKEND_URL || ''}/api`;
const abonnes = new Set();

// 1.54.2.0 (signalement du club "je vois le popup de redemarrage en boucle") : sur un telephone,
// chaque sortie de veille, changement Wi-Fi/4G ou onglet mis en arriere-plan fait echouer les
// appels automatiques (compteurs toutes les 30 s) en "erreur reseau" alors que le serveur va tres
// bien - l'ecran s'ouvrait puis se refermait a chaque fois. Et une erreur 502/503 VOULUE par
// l'application (ex. "Google Drive non configure", reponse JSON) le declenchait aussi.
// Desormais :
//  - seule la page de secours du proxy compte (502/503/504 sans corps JSON), ou l'absence totale
//    de reponse ;
//  - rien quand l'onglet est cache ou que le navigateur se sait hors ligne ;
//  - avant d'ouvrir, /api/health est interroge deux fois a 2 s d'intervalle : l'ecran ne s'ouvre
//    que si le serveur ne repond vraiment pas.
export const estIndisponibilite = (error) => {
  const statut = error?.response?.status;
  if ([502, 503, 504].includes(statut)) {
    const type = String(error.response.headers?.['content-type'] || '');
    return !type.includes('application/json');
  }
  return !error?.response && error?.code === 'ERR_NETWORK';
};

const serveurRepond = async () => {
  const controle = new AbortController();
  const minuterie = setTimeout(() => controle.abort(), 6000);
  try {
    const r = await fetch(`${API}/health`, { cache: 'no-store', signal: controle.signal });
    return r.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(minuterie);
  }
};

let verificationEnCours = false;
const confirmerPuisSignaler = async () => {
  if (verificationEnCours) return;
  verificationEnCours = true;
  try {
    if (await serveurRepond()) return;
    await new Promise(res => setTimeout(res, 2000));
    if (document.visibilityState === 'hidden' || navigator.onLine === false) return;
    if (await serveurRepond()) return;
    abonnes.forEach(f => f());
  } finally {
    verificationEnCours = false;
  }
};

export const signalerSiIndisponible = (error) => {
  if (!estIndisponibilite(error)) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  confirmerPuisSignaler();
};

// Les pages publiques (connexion, mot de passe oublie...) appellent axios directement : meme
// detection pour elles. Les instances creees par axios.create() n'heritent pas de cet
// intercepteur, d'ou l'appel explicite dans AuthContext.
axios.interceptors.response.use((r) => r, (error) => { signalerSiIndisponible(error); return Promise.reject(error); });

const RedemarrageServeur = () => {
  const [ouvert, setOuvert] = useState(false);
  const [masque, setMasque] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const f = () => { setOuvert(true); };
    abonnes.add(f);
    return () => { abonnes.delete(f); };
  }, []);

  useEffect(() => {
    if (!ouvert) return undefined;
    let fini = false;
    const verifier = async () => {
      // Toute reponse 200 suffit : le proxy renvoie 502 tant que le serveur ne repond pas.
      if (await serveurRepond() && !fini) {
        setOuvert(false);
        setMasque(false);
        toast({ title: 'L’espace adhérent est de nouveau disponible', description: 'Si une action vient d\'échouer, recommencez-la.' });
      }
    };
    const t = setInterval(verifier, 3000);
    return () => { fini = true; clearInterval(t); };
  }, [ouvert]);

  if (!ouvert) return null;
  if (masque) {
    return (
      <div role="status" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] rounded-full border bg-card px-4 py-2 shadow-sm flex items-center gap-2 text-sm" data-testid="redemarrage-bandeau">
        <RefreshCw className="w-4 h-4 animate-spin motion-reduce:animate-none" />Mise à jour en cours, reconnexion automatique…
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4" data-testid="redemarrage-ecran">
      <div role="alertdialog" aria-modal="true" aria-labelledby="redemarrage-titre" aria-describedby="redemarrage-texte"
        className="max-w-md w-full rounded-lg border bg-card p-6 space-y-3">
        <h2 id="redemarrage-titre" className="text-[1.375rem] font-semibold flex items-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin motion-reduce:animate-none" />Mise à jour en cours
        </h2>
        <p id="redemarrage-texte" className="text-muted-foreground">
          L’espace adhérent redémarre, en général en moins d'une minute. Cette fenêtre se fermera toute seule dès son retour ;
          votre page et vos saisies restent en place. Si elle ne se ferme pas, vérifiez votre connexion internet.
        </p>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setMasque(true)} data-testid="redemarrage-masquer">Continuer à lire la page</Button>
        </div>
      </div>
    </div>
  );
};

export default RedemarrageServeur;
